import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";
import clsx from "clsx";

import WordmarkLight from "Assets/wone_logo.svg";
import LanguageMenu from "components/language-menu/language-menu";

import styles from "./auth-layout.module.scss";

export type AuthLayoutVariant = "intro" | "form";

export interface AuthLayoutProps {
  variant: AuthLayoutVariant;
  /**
   * Form variant only — when omitted, the back chevron falls back to
   * ``/email-entry`` (the auth funnel's stable root) rather than
   * ``navigate(-1)``. Deep-link arrivals have no SPA history to pop, so
   * ``navigate(-1)`` would silently exit the app.
   */
  onBack?: () => void;
  /** Form variant only — hide the back button (e.g. on the top of an auth flow). */
  hideBack?: boolean;
  /** Intro variant only — tagline rendered under the wordmark. */
  tagline?: React.ReactNode;
  /** Intro variant only — video sources. When provided, replaces the poster image. */
  videoSources?: ReadonlyArray<{ src: string; type: string }>;
  className?: string;
  children?: React.ReactNode;
}

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Looping intro video with mobile-parity fade behaviour:
 * - autoplay, muted, looped, playsInline
 * - 1s fade-in from black on first paint
 * - 0.5s crossfade through black at the loop seam (no hard cut)
 * Fades go to/from the `.intro` container's black background so there's
 * no static placeholder image flashing at the loop seam.
 */
const VideoBackground: React.FC<{
  sources: ReadonlyArray<{ src: string; type: string }>;
}> = ({ sources }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [opacity, setOpacity] = useState(0);
  const [transitionMs, setTransitionMs] = useState(1000);
  const lastTimeRef = useRef(0);
  const didFadeOutThisLoopRef = useRef(false);

  const handleCanPlay = (): void => {
    // First-paint fade-in over 1s.
    setTransitionMs(1000);
    setOpacity(1);
  };

  const handleTimeUpdate = (): void => {
    const v = videoRef.current;
    if (!v) return;
    const { currentTime } = v;
    const duration = v.duration;
    if (!duration || duration <= 1) return;

    // Loop wrap detected by a backward jump in currentTime — more reliable
    // than the `loop` event, which doesn't fire consistently across
    // browsers when `loop` attribute is set on the element.
    if (currentTime < lastTimeRef.current - 0.5) {
      didFadeOutThisLoopRef.current = false;
      setTransitionMs(500);
      setOpacity(1);
    } else if (
      !didFadeOutThisLoopRef.current &&
      duration - currentTime <= 0.5
    ) {
      didFadeOutThisLoopRef.current = true;
      setTransitionMs(500);
      setOpacity(0);
    }

    lastTimeRef.current = currentTime;
  };

  return (
    <video
      ref={videoRef}
      className={styles["intro-media"]}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      onCanPlay={handleCanPlay}
      onTimeUpdate={handleTimeUpdate}
      style={{
        opacity,
        transition: `opacity ${transitionMs}ms ease`,
      }}
    >
      {sources.map((source) => (
        <source key={source.src} src={source.src} type={source.type} />
      ))}
    </video>
  );
};

const IntroLayout: React.FC<AuthLayoutProps> = ({
  tagline,
  videoSources,
  className,
  children,
}) => {
  const hasVideo = videoSources && videoSources.length > 0;
  return (
    <div className={clsx(styles["intro"], className)}>
      {hasVideo && <VideoBackground sources={videoSources} />}
      {/* No-video fallback intentionally renders nothing — the `.intro`
          container's black background covers the screen. */}
      <div className={styles["intro-overlay"]} />
      <div className={styles["intro-content"]}>
        <div className={styles["intro-headline"]}>
          <img
            className={styles["intro-wordmark"]}
            src={WordmarkLight}
            alt="WONE"
          />
          {tagline && <h1 className={styles["intro-tagline"]}>{tagline}</h1>}
        </div>
        <div className={styles["intro-actions"]}>{children}</div>
      </div>
    </div>
  );
};

// Form variant intentionally kept inline below — no shared state, just a
// container with the top-bar chrome.

const FormLayout: React.FC<AuthLayoutProps> = ({
  onBack,
  hideBack,
  className,
  children,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const handleBack = (): void => {
    if (onBack) {
      onBack();
      return;
    }
    // Velislav/Ed review on #1171: when a consumer didn't pass onBack
    // explicitly, ``navigate(-1)`` would exit the SPA for any user who
    // arrived via a deep link (no SPA history to pop). Send them to
    // ``/email-entry`` instead — the auth funnel's stable root.
    navigate("/email-entry");
  };

  return (
    <div className={clsx(styles["form"], className)}>
      <header className={styles["top-bar"]}>
        {hideBack ? (
          <span aria-hidden="true" />
        ) : (
          <button
            type="button"
            className={styles["back-button"]}
            onClick={handleBack}
            aria-label={t("layout.goBack")}
          >
            <ChevronLeftIcon className={styles["back-icon"]} />
          </button>
        )}
        <img
          className={styles["top-bar-wordmark"]}
          src={WordmarkLight}
          alt="WONE"
        />
        <LanguageMenu className={styles["top-bar-language"]} />
      </header>
      <main className={styles["form-body"]}>
        <div className={styles["form-column"]}>{children}</div>
      </main>
    </div>
  );
};

export const AuthLayout: React.FC<AuthLayoutProps> = (props) => {
  return props.variant === "intro" ? (
    <IntroLayout {...props} />
  ) : (
    <FormLayout {...props} />
  );
};

export default AuthLayout;
